import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { find, get, map, pull, values, isEmpty } from 'lodash'

import { centsToDollars } from '../../utils/formatters'

import { getModifiersWithItems } from '../../utils/orderUtils'

import RoundButton from '../RoundButton'

import styles from './RequestRefundModal.module.scss'
import { ReactComponent as CheckmarkIcon } from '../../assets/icons/checkmark-icon.svg'

// Store comped boolean separately so that reasons and consumption are not intertwined.
const refundReasons = {
  '1': { id: '1', reason: 'Incorrectly entered order', isComp: false },
  '2': { id: '2', reason: 'Insufficient inventory', isComp: false },
  '3': { id: '3', reason: 'Item returned, quality related', isComp: false },
  '4': { id: '4', reason: 'Guest changed their mind', isComp: false },
  '5': { id: '5', reason: 'Long wait for item', isComp: false },
  '6': { id: '6', reason: 'Comped items', isComp: true },
}

const RequestRefundModal = ({ refund, order, edit, onComplete, onClose, className, ...props }) => {
  const classNames = cn(styles.requestRefundModal, className)
  const items = get(order, 'itemModels', [])
  const [mouseDown, setMouseDown] = useState(false)
  const [mouseMoving, setMouseMoving] = useState(false)
  const [refundAmount, setRefundAmount] = useState(0)
  const [refundReason, setRefundReason] = useState(undefined)
  const [editMode, setEditMode] = useState(edit)


  var itemsWithModifiers = getModifiersWithItems(items)
  const totalInCents = itemsWithModifiers.totalCostInCents
  const allItems = itemsWithModifiers.items

  const itemIdIndexes = allItems.map((_, index) => index)
  const [selectedItems, setSelectedItems] = useState(itemIdIndexes)
  const isEntireOrder = itemIdIndexes.length === selectedItems.length

  useEffect(() => {
    const sumSelectedItems = (accumulator, itemIndex) => accumulator + allItems[itemIndex].priceInCents
    const amountToRefundInCents = selectedItems.reduce(sumSelectedItems, 0)
    setRefundAmount(amountToRefundInCents)
  }, [selectedItems, allItems])

  useEffect(() => {
    setEditMode(edit)
  }, [edit])

  useEffect(() => {
    if (!editMode) {
      const refundableItems = allItems.reduce((accumulator, item, index) => {
        return item.refund ? [...accumulator, index] : [...accumulator]
      }, [])

      setSelectedItems(refundableItems)

      if (refund && refund.reason) {
        const reason = find(values(refundReasons), { reason: refund.reason })
        setRefundReason(reason)
      }
    }
  // TODO: We need to audit the exhaustive dependencies here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, allItems])

  const selectIfSwiping = (index) => {
    if (mouseMoving) checkItem(index)
  }

  const checkItem = (index) => {
    const isChecked = selectedItems.includes(index)
    if (isChecked || !editMode) return

    let x = []
    for (let i = 0; i < allItems[index].totalModifiersWithCost; i++) {
      x.push(index + 1)
    }

    setSelectedItems([index, ...x, ...selectedItems])
  }

  const uncheckItem = (index) => {
    if (mouseDown || !editMode) return

    let x = []
    for (let i = 0; i < allItems[index].totalModifiersWithCost; i++) {
      x.push(index + 1)
    }

    setSelectedItems([...pull(selectedItems, index, ...x)])
  }

  const onSelectEntireOrder = () => {
    if (!editMode) return

    if (isEntireOrder) {
      setSelectedItems([])
    } else {
      setSelectedItems([...itemIdIndexes])
    }
  }

  const refundReasonChanged = (refundReasonId) => {
    const refundReason = refundReasons[refundReasonId]
    setRefundReason(refundReason)
  }

  const handleOnComplete = () => {
    const orderId = order.uuid
    const itemIndexes = selectedItems
    const itemIds = map(selectedItems, (itemIndex) => allItems[itemIndex].id)
    const lineItemIds = map(selectedItems, (itemIndex) => allItems[itemIndex].lineItemUuid)
    const reason = refundReason.reason
    const isComp = refundReason.isComp

    onComplete({ orderId, itemIndexes, itemIds, lineItemIds, reason, isComp })
  }

  const renderItem = (item, index) => {
    const isChecked = selectedItems.includes(index)
    const onChange = isChecked ? uncheckItem : checkItem

    return (
      <label
        className={cn(styles.itemLabel, (item.isModifier) ? styles.modifierLabel : '')}
        key={`${item.name}-${index}`}
        onMouseMove={() => selectIfSwiping(index)}
      >
        {`(1) ${item.name} - ${item.priceInCents ? centsToDollars(item.priceInCents) : centsToDollars(item.defaultPriceInCents)}`}
        <input type="checkbox" className={styles.checkbox} checked={isChecked} onChange={() => onChange(index)} />
        <span className={styles.checkmark}>
          <CheckmarkIcon className={styles.checkmarkIcon} />
        </span>
      </label>
    )
  }

  const renderRefundReasons = () => {
    return (
      <select className={styles.refundReason} onChange={(event) => refundReasonChanged(event.target.value)} value={get(refundReason, 'id')}>
        <option value={undefined}>Select a refund reason</option>
        {values(refundReasons).map((refund) => (
          <option value={refund.id}>{refund.reason}</option>
        ))}
      </select>
    )
  }

  return (
    <div {...props} className={classNames}>
      <div className={styles.overlay} onClick={onClose}></div>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1>{`Order # ${order.orderNumber} Refund Request`}</h1>
        </div>
        <div
          className={styles.body}
          onMouseDown={() => {
            setMouseDown(true)
          }}
          onMouseUp={() => {
            setMouseDown(false)
            setMouseMoving(false)
          }}
          onMouseLeave={() => {
            setMouseDown(false)
            setMouseMoving(false)
          }}
          onMouseMove={() => {
            if (mouseDown) setMouseMoving(true)
          }}
        >
          <label className={cn(styles.itemLabel, styles.entireOrderLabel)}>
            {`Entire Order - ${centsToDollars(totalInCents)}`}
            <input type="checkbox" className={styles.checkbox} checked={isEntireOrder} onChange={onSelectEntireOrder} />
            <span className={styles.checkmark}>
              <CheckmarkIcon className={styles.checkmarkIcon} />
            </span>
          </label>
          {map(allItems, renderItem)}
          {renderRefundReasons()}
          <div className={styles.footer}>
            <RoundButton
              className={styles.cancelButton}
              text="Close"
              onClick={onClose}
            />
            {editMode && <RoundButton
              className={styles.refundButton}
              text={`Refund ${centsToDollars(refundAmount)}`}
              disabled={refundReason === undefined || isEmpty(selectedItems)}
              onClick={() => {
                handleOnComplete()
                onClose()
              }}
            />}
          </div>
        </div>
      </div>
    </div>
  )
}

RequestRefundModal.defaultProps = {
  order: {},
	onComplete: () => {},
  onClose: () => {},
  edit: true,
}

RequestRefundModal.propTypes = {
  order: PropTypes.object,
	onComplete: PropTypes.func,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
}

export default RequestRefundModal
